import api from "@/lib/api/axios";
import axios from "axios";
import { getOAuthRedirectUri } from "@/lib/utils";

// Request/Response Types based on API documentation
interface TokenObtainPairRequest {
  username: string;
  password: string;
}

interface TokenObtainPairResponse {
  access: string;
  refresh: string;
}

interface TokenRefreshResponse {
  access: string;
}

interface GoogleAuthResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  last_login: string;
}

interface GoogleUrlResponse {
  url: string;
}

interface UserMeResponse {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  avatar_url?: string;
}

export const authApi = {
  /**
   * Takes a set of user credentials and returns an access and refresh JSON web
   * token pair to prove the authentication of those credentials.
   *
   * @param data - Object containing username and password
   */
  obtainToken: async (
    data: TokenObtainPairRequest,
  ): Promise<TokenObtainPairResponse> => {
    const response = await api.post<TokenObtainPairResponse>(
      "/api/auth/token/",
      data,
    );
    return response.data;
  },

  /**
   * Takes a refresh type JSON web token and returns an access type JSON web
   * token if the refresh token is valid.
   *
   * @param refreshToken - The refresh token
   */
  refreshToken: async (refreshToken: string): Promise<TokenRefreshResponse> => {
    const response = await api.post<TokenRefreshResponse>(
      "/api/auth/token/refresh/",
      {
        refresh: refreshToken,
      },
    );
    return response.data;
  },

  /**
   * Takes a token and indicates if it is valid.
   *
   * @param token - The token to verify
   */
  verifyToken: async (token: string): Promise<void> => {
    await api.post("/api/auth/token/verify/", {
      token,
    });
    // If no error is thrown, the token is valid
  },

  /**
   * Get the Google OAuth2 authorization URL
   *
   * @param redirectUri - Optional custom redirect URI for testing
   */
  getGoogleUrl: async (redirectUri?: string): Promise<GoogleUrlResponse> => {
    // Use environment-specific redirect URI if none provided
    const finalRedirectUri = redirectUri || getOAuthRedirectUri();
    const params = { redirect_uri: finalRedirectUri };
    const response = await api.get<GoogleUrlResponse>("/api/auth/google/url/", {
      params,
    });
    return response.data;
  },

  /**
   * Authenticate with Google OAuth2 using authorization code
   *
   * @param code - The authorization code from Google OAuth
   */
  googleAuth: async (code: string): Promise<GoogleAuthResponse> => {
    try {
      const redirectUri = getOAuthRedirectUri();
      const response = await api.post<GoogleAuthResponse>(
        "/api/auth/google/",
        { code, redirect_uri: redirectUri },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to authenticate with Google",
        );
      }
      throw error;
    }
  },

  /**
   * Get current authenticated user's profile
   */
  getCurrentUser: async (): Promise<UserMeResponse> => {
    const response = await api.get<UserMeResponse>("/api/auth/me/");
    return response.data;
  },

  /**
   * Request account deletion by email
   *
   * @param email - The email address to send deletion request to
   */
  requestAccountDeletion: async (email: string): Promise<void> => {
    await api.post("/api/auth/request-account-deletion/", { email });
  },
};

// Export types for use in other files
export type {
  TokenObtainPairRequest,
  TokenObtainPairResponse,
  TokenRefreshResponse,
  GoogleAuthResponse,
  GoogleUrlResponse,
  UserMeResponse,
};
